import { ComponentPropsWithoutRef, ReactNode } from "react";
import GradientBorderRadius, {
  ActiveStyles,
  HoverStyles,
  RadiusStyles,
  GradientStyles,
  BackgroundStyles,
} from "./gradient-border-radius";
import clsx from "clsx";
import { Spinner } from "./spinner";
import { IconComponent } from "./icon";

export type ButtonProps = ComponentPropsWithoutRef<"button"> & {
  children?: ReactNode | null;
  title?: string;
  radius?: keyof typeof RadiusStyles;
  height?: string;
  enabled?: boolean;
  hover?: boolean;
  hoverStyle?: keyof typeof HoverStyles;
  active?: boolean;
  activeStyle?: keyof typeof ActiveStyles;
  textColor?: string;
  onClick?: () => void;
  loading?: boolean;
  success?: boolean;
  gradient?: keyof typeof GradientStyles;
  background?: keyof typeof BackgroundStyles;
};

export const Button = ({
  title = undefined,
  children = null,
  radius = "bigger",
  height = "h-[1.375rem]",
  enabled = true,
  hover = true,
  hoverStyle = "r-100",
  active = true,
  activeStyle = "b-50",
  textColor = "text-white",
  onClick = undefined,
  loading = false,
  success = false,
  gradient = "default-border",
  background = "default",
  ...rest
}: ButtonProps) => {
  const shouldWrapParentHeight = height.includes("h-full");

  return (
    <GradientBorderRadius
      hover={hover ? hoverStyle : undefined}
      hoverStyle={hoverStyle}
      background={background}
      gradient={gradient}
      radius={radius}
      active={active ? activeStyle : undefined}
      className={clsx(
        rest?.className && rest.className,
        !rest?.className && "w-full text-sm",
        shouldWrapParentHeight ? "h-full" : height,
        "cursor-default"
      )}
    >
      <button
        {...rest}
        disabled={!enabled}
        onClick={onClick}
        className="w-full h-full"
      >
        <div
          className={clsx(
            `flex items-center justify-center h-full ${textColor || "text-white"}`
          )}
        >
          {loading ? (
            <Spinner />
          ) : success ? (
            <IconComponent name="check" className="w-[0.875rem] h-[0.875rem]" />
          ) : (
            <>
              {title}
              {children}
            </>
          )}
        </div>
      </button>
    </GradientBorderRadius>
  );
};
